/**
 * Seletor de ciclo de cobrança (Mensal · Trimestral · Semestral · Anual).
 *
 * O desconto ao lado do nome **vem do servidor** (`discountPercent`). Não há
 * `-20%` escrito no JSX: rótulo fixo continua certo na tela e errado no extrato
 * depois do primeiro reajuste.
 *
 * Quando os planos têm descontos diferentes no mesmo ciclo, o rótulo vira
 * "até 8% off" — continua sendo um número do servidor, só que dito de um jeito
 * que é verdade para todos os planos ao mesmo tempo.
 *
 * É um `radiogroup` de verdade: setinha do teclado navega, leitor de tela
 * anuncia o que está escolhido, e o alvo de toque é de 44px no celular.
 */
import { cn } from '@mgdlead/ui';

import { ciclosDisponiveis, descontoDoCiclo, rotuloDeDesconto } from './billing-ui';
import type { BillingInterval, CatalogPlan } from '../../lib/api';

export interface CicloSelectorProps {
  plans: readonly CatalogPlan[];
  value: BillingInterval;
  onChange: (interval: BillingInterval) => void;
  className?: string;
}

export function CicloSelector({ plans, value, onChange, className }: CicloSelectorProps) {
  const ciclos = ciclosDisponiveis(plans);
  if (ciclos.length <= 1) return null;

  return (
    <div
      role="radiogroup"
      aria-label="Ciclo de cobrança"
      className={cn(
        'grid grid-cols-2 gap-2 rounded-lg border border-border bg-surface p-1.5',
        'sm:inline-grid sm:auto-cols-fr sm:grid-flow-col',
        className,
      )}
    >
      {ciclos.map((ciclo) => {
        const desconto = descontoDoCiclo(plans, ciclo.interval);
        const escolhido = ciclo.interval === value;

        return (
          <button
            key={ciclo.interval}
            type="button"
            role="radio"
            aria-checked={escolhido}
            onClick={() => onChange(ciclo.interval)}
            className={cn(
              'flex min-h-11 flex-col items-center justify-center gap-0.5 rounded-md border px-3 py-1.5',
              'text-sm font-medium transition-colors duration-[var(--duration-micro)]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              escolhido
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-transparent text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            <span>{ciclo.label}</span>
            {desconto ? (
              <span
                className={cn(
                  'nums text-2xs font-medium',
                  escolhido ? 'text-primary-foreground/85' : 'text-success',
                )}
              >
                {rotuloDeDesconto(desconto)}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
